"use client";

import { CustomCategory } from "@/components/category";
import { Content } from "@/services/content/types";
import { ITEM_FEED_LIMIT } from "@/utils/constants";
import groupItemsByDate from "@/utils/groupItemsByDate";
import { createImageSizes, createImageUrl } from "@/utils/image";
import { prefixSlug } from "@/utils/url";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import FormatDate from "./format-date";

type ItemFeedProps = {
  limit?: number;
  items: Content.Entry[];
  total?: number;
  initialPage?: number;
  /** Base path for path-based pagination, e.g. "/av/martin". When set,
   *  page 1 links to `basePath`, page 2+ links to `basePath/2`. */
  basePath?: string;
  groupByDate?: boolean;
  loadMore: (
    page: number,
  ) => Promise<Content.Response<Content.Entry> | undefined>;
};

type ItemProps = {
  item: {
    slug?: string;
    createdAt: string;
    categoryName?: string;
    categoryColor?: Content.CategoryColors;
    title?: string;
    excerpt?: string;
    image?: Content.Asset;
    type?: string;
    fulldate?: boolean;
  };
};

export const Item = (props: ItemProps) => {
  const isPodcast = props.item.type === "podcast-episode";
  const isPlay = props.item.type === "play-episode";
  return (
    <li className="py-5 last:pb-0">
      <Link
        className="group flex space-x-6 items-start"
        href={props.item.slug!}
      >
        <div className="flex-1">
          <div className="font-bold text-gray-400 mb-2">
            {isPodcast || isPlay ? (
              <FormatDate date={props.item.createdAt} format="relative" />
            ) : props.item.fulldate ? (
              <>
                <FormatDate date={props.item.createdAt} format="relative" />{" "}
                <FormatDate date={props.item.createdAt} format="time" />
              </>
            ) : (
              <FormatDate date={props.item.createdAt} format="time" />
            )}
          </div>

          <div className="flex-1">
            {props.item.categoryColor && props.item.categoryName && (
              <CustomCategory
                color={props.item.categoryColor}
                name={props.item.categoryName}
              />
            )}
            {props.item.title && (
              <h3 className="text-lg md:text-xl font-semibold group-hover:underline">
                {props.item.title}
              </h3>
            )}

            {props.item.excerpt && (
              <span className="block mt-4 text-gray-400 line-clamp-3">
                {props.item.excerpt}
              </span>
            )}
          </div>
        </div>
        {props.item.image?.fields.file?.url && (
          <div
            className={classNames("relative flex-shrink-0 mt-2", {
              "w-[80px] h-[80px] md:w-[180px] md:h-[180px]": isPodcast,
              "w-[120px] h-[68px] md:w-[180px] md:h-[100px]": !isPodcast,
            })}
          >
            <Image
              fill
              sizes={createImageSizes({
                desktop: "33vw",
                tablet: "33vw",
                mobile: "33vw",
              })}
              src={createImageUrl(props.item.image.fields.file.url, {
                crop: isPodcast ? "1:1" : "16:9",
              })}
              alt={props.item.image.fields.alt || ""}
              className=""
            />
          </div>
        )}
      </Link>
    </li>
  );
};

const ItemFeed = (props: ItemFeedProps) => {
  const startPage = props.initialPage ?? 1;
  const limit = props.limit || ITEM_FEED_LIMIT;

  const [page, setPage] = useState(startPage);
  const [items, setItems] = useState<Content.Entry[]>(props.items);
  const [hasMore, setHasMore] = useState(props.items.length >= limit);
  const [isLoading, setIsLoading] = useState(false);

  const fetchItems = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const res = await props.loadMore(page);

      if (res?.items) {
        const more = res.items.length >= (props.limit || ITEM_FEED_LIMIT);
        const nextPage = page + 1;

        setHasMore(more);
        setPage(nextPage);
        setItems([...items, ...res.items]);

        const nextHref = props.basePath
          ? nextPage === 1
            ? props.basePath
            : `${props.basePath}/${nextPage}`
          : (() => {
              const url = new URL(window.location.href);
              url.searchParams.set("page", String(nextPage));
              return url.toString();
            })();
        window.history.pushState(null, "", nextHref);
      } else {
        setHasMore(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const groupedItems = props.groupByDate ? groupItemsByDate(items) : null;
  const renderItem = (item: Content.Entry) => {
    if (item.sys.contentType.sys.id === "article") {
      const article = item as Content.Article;
      return (
        <Item
          key={article.sys.id}
          item={{
            slug: prefixSlug(
              article.fields.slug,
              article.sys.contentType.sys.id,
            ),
            fulldate: !props.groupByDate,
            title: article.fields.title,
            image: article.fields.images?.[0],
            categoryName: article.fields.categories?.[0]?.fields?.name,
            categoryColor: article.fields.categories?.[0]?.fields?.color,
            createdAt: article.sys.createdAt,
            type: article.sys.contentType.sys.id,
          }}
        />
      );
    }

    if (item.sys.contentType.sys.id === "play-episode") {
      const playEpisode = item as Content.Play;
      const playShow = playEpisode.fields["play-show"];
      return (
        <Item
          key={playEpisode.sys.id}
          item={{
            slug: prefixSlug(
              playEpisode.fields.slug,
              playEpisode.sys.contentType.sys.id,
            ),
            title: playEpisode.fields.title,
            image: playEpisode.fields.image,
            categoryName: playShow?.fields.title,
            categoryColor: "Play",
            createdAt: playEpisode.fields.broadcastStart || "1979-01-01T00:00",
            type: playEpisode.sys.contentType.sys.id,
          }}
        />
      );
    }

    if (item.sys.contentType.sys.id === "podcast-episode") {
      const podcastEpisode = item as Content.PodcastEpisode;
      const podcastShow = podcastEpisode.fields["podcast-show"];
      return (
        <Item
          key={podcastEpisode.sys.id}
          item={{
            slug: prefixSlug(
              podcastEpisode.fields.slug,
              podcastEpisode.sys.contentType.sys.id,
            ),
            categoryName: podcastShow?.fields.title,
            categoryColor: "Podd",
            title: podcastEpisode.fields.title,
            excerpt: podcastEpisode.fields.excerpt,
            image: podcastEpisode.fields.image,
            createdAt: podcastEpisode.sys.createdAt,
            type: podcastEpisode.sys.contentType.sys.id,
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      <div className="divide-y divide-sand-700 ">
        {groupedItems ? (
          Object.keys(groupedItems)
            .sort((a, b) => b.localeCompare(a))
            .map((date) => (
              <div className="py-7 md:py-14" key={date}>
                <h2 className="font-bold text-xl font-noto-sans bg-sand-600 p-4 rounded-sm">
                  <FormatDate date={date} format="relative" />
                </h2>
                <ul className="divide-y divide-sand-700 ">
                  {groupedItems[date].map(renderItem)}
                </ul>
              </div>
            ))
        ) : (
          <ul className="divide-y divide-sand-700 ">
            {items.map(renderItem)}
          </ul>
        )}
      </div>

      {hasMore && (
        <div className="flex justify-center pt-8 pb-4">
          <button
            type="button"
            onClick={fetchItems}
            disabled={isLoading}
            className="bg-sand-700 btn disabled:opacity-50"
          >
            {isLoading ? "Laddar..." : "Ladda fler"}
          </button>
        </div>
      )}
    </>
  );
};

export default ItemFeed;
